import Typography from '../../../../shared/constants/Typography';
import { Point } from '../../../../shared/components/modals/SignatureModal/SignatureModalProps';

/**
 * Calculates the distance between two points (a and b).
 * @param a - The starting point {x, y}.
 * @param b - The ending point {x, y}.
 * @returns The distance between the points.
 */
export const distance = (a: Point, b: Point) =>
  Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Computes the total physical length of a stroke (or series of connected points) by summing
 * the distances between consecutive points.
 * @param points - An array of points representing a single stroke.
 * @returns The total length of the ink used in the stroke.
 */
export const computeInkLength = (points: Point[]) => {
  let len = 0;
  for (let i = 1; i < points.length; i++) {
    len += distance(points[i - 1], points[i]);
  }
  return len;
};

/**
 * Calculates the minimum bounding box that encloses all provided points.
 * Used to determine the overall spatial footprint (width and height) of the signature.
 * @param points - An array of all points across all strokes.
 * @returns An object containing the width and height of the bounding box.
 */
export const computeBoundingBox = (points: Point[]) => {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  points.forEach(p => {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  });

  return { width: maxX - minX, height: maxY - minY };
};

/**
 * Computes the maximum deviation of a path from a straight line drawn between its start and end points.
 * This heuristic helps determine if the drawing is a complex curve/signature or just a simple, straight line.
 * @param points - An array of points representing a stroke or entire signature path.
 * @returns The maximum distance any point deviates from the start-to-end line.
 */
export const computeDeviation = (points: Point[]) => {
  if (points.length < 2) return 0;

  const start = points[0];
  const end = points[points.length - 1];

  const dx = end.x - start.x;
  const dy = end.y - start.y;

  let maxDev = 0;

  points.forEach(p => {
    const t =
      ((p.x - start.x) * dx + (p.y - start.y) * dy) / (dx * dx + dy * dy);

    const projX = start.x + t * dx;
    const projY = start.y + t * dy;

    maxDev = Math.max(maxDev, distance(p, { x: projX, y: projY }));
  });

  return maxDev;
};

/**
 * Validates the entire collection of drawn strokes against predefined business rules
 * to ensure the input is substantial enough to be considered a valid signature.
 * It updates the parent component's state regarding validity and feedback message.
 * @param allStrokes - A 2D array of points representing all completed strokes.
 * @param setValid - State setter function to mark the signature as valid/invalid.
 * @param setMsg - State setter function to display feedback messages to the user.
 */
export const validate = (
  allStrokes: Point[][],
  setValid: React.Dispatch<React.SetStateAction<boolean>>,
  setMsg: React.Dispatch<React.SetStateAction<string>>,
  t: any
) => {
  const allPoints = allStrokes.flat();
  const inkLen = computeInkLength(allPoints);
  const bbox = computeBoundingBox(allPoints);

  let significant = 0;
  let tiny = 0;

  allStrokes.forEach(s => {
    const len = computeInkLength(s);
    if (len > 20) significant++;
    else tiny++;
  });

  const deviation = computeDeviation(allPoints);

  const isValid =
    inkLen >= Typography.signatureDimensions.MIN_TOTAL_INK_LENGTH &&
    significant >=
      Typography.signatureDimensions.MIN_SIGNIFICANT_STROKES_REQUIRED &&
    tiny <= Typography.signatureDimensions.MAX_ALLOWED_TINY_STROKES &&
    bbox.width >= Typography.signatureDimensions.MIN_OVERALL_WIDTH &&
    bbox.height >= Typography.signatureDimensions.MIN_OVERALL_HEIGHT &&
    deviation > 6;

  setValid(isValid);
  setMsg(isValid ? t('validSignature') : t('invalidSignature'));
};
